import React from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Loader2 } from 'lucide-react';

/**
 * 🛠️ cn Utility
 * Merges Tailwind classes safely, resolving conflicts (e.g., padding vs. px).
 */
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' | 'glass';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
}

/**
 * 🧱 Standardized EverydayOS Button
 * A tactile, state-aware button component with standardized 
 * visual styles for all household features.
 */
export const Button: React.FC<ButtonProps> = ({
  className,
  variant = 'primary',
  size = 'md',
  isLoading,
  children,
  disabled,
  ...props
}) => {
  const variants = {
    // Standard Indigo/Indigo-Glow look
    primary: 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 active:scale-[0.97]',
    // Slate based, fits in Sidebars and Toolbars
    secondary: 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-[0.97]',
    // Rose based, used for Deletions and High-Alerts
    danger: 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-500 hover:text-white transition-all active:scale-[0.97]',
    // Bare text, fits in headers
    ghost: 'bg-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white',
    // 2px border, used for secondary actions like "Reset Layout"
    outline: 'border-2 border-slate-200 dark:border-slate-800 bg-transparent hover:border-indigo-500 dark:hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-700 dark:text-slate-300',
    // Frosted glass look for dashboard overlays
    glass: 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 active:scale-[0.97]',
  };

  const sizes = {
    sm: 'h-8 px-4 text-[10px] rounded-xl font-black uppercase tracking-widest',
    md: 'h-11 px-6 rounded-2xl text-[11px] font-black uppercase tracking-widest',
    lg: 'h-14 px-8 text-sm rounded-[1.5rem] font-black uppercase tracking-widest',
    icon: 'h-10 w-10 rounded-xl p-0', // Perfect for square icon-only buttons
  };

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center transition-all duration-300',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-40 disabled:grayscale',
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          <span className="opacity-50 italic lowercase">syncing...</span>
        </>
      ) : (
        children
      )}
    </button>
  );
};